import { withAlpha, useAccentFn } from "@/lib/color";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import TavolinoBadge from "@/components/TavolinoBadge";

function fmtDateHeader(iso) {
    if (!iso) return "Data da definire";
    try {
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch { return iso; }
}

function fmtShort(iso) {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}`;
}

function TeamCell({ name, logo, align = "right" }) {
    return (
        <div className={`flex items-center gap-2 min-w-0 ${align === "right" ? "justify-end" : "justify-start"}`}>
            {align === "right" && <span className="truncate text-tffffff text-sm">{name}</span>}
            {logo ? (
                <img src={logo} alt="" className="w-6 h-6 object-contain shrink-0" />
            ) : (
                <span className="w-6 h-6 shrink-0 rounded-full bg-t16181c border border-t22252a" />
            )}
            {align === "left" && <span className="truncate text-tffffff text-sm">{name}</span>}
        </div>
    );
}

export default function Calendario() {
    const acc = useAccentFn();
    const [data, setData] = useState({ matches: [], competitions: [], teams: [] });
    const [loading, setLoading] = useState(true);
    const [kind, setKind] = useState("all");           // all | league | cup
    const [competitionId, setCompetitionId] = useState("all");
    const [teamId, setTeamId] = useState("all");
    const [status, setStatus] = useState("all");        // all | scheduled | played
    const [dFrom, setDFrom] = useState("");
    const [dTo, setDTo] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/calendar");
                setData(data || { matches: [], competitions: [], teams: [] });
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const competitions = useMemo(() => {
        if (kind === "all") return data.competitions;
        return data.competitions.filter((c) => c.kind === kind);
    }, [data.competitions, kind]);

    const filtered = useMemo(() => {
        return (data.matches || []).filter((m) => {
            if (kind !== "all" && m.kind !== kind) return false;
            if (competitionId !== "all" && m.competition_id !== competitionId) return false;
            if (teamId !== "all" && m.home_team_id !== teamId && m.away_team_id !== teamId) return false;
            if (status !== "all" && m.status !== status) return false;
            if (dFrom && (m.date_from || "9999") < dFrom) return false;
            if (dTo && (m.date_from || "0000") > dTo) return false;
            return true;
        });
    }, [data.matches, kind, competitionId, teamId, status, dFrom, dTo]);

    // raggruppa per data
    const groups = useMemo(() => {
        const map = new Map();
        for (const m of filtered) {
            const key = m.date_from || "zzzz";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(m);
        }
        return [...map.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
    }, [filtered]);

    const resetFilters = () => {
        setKind("all"); setCompetitionId("all"); setTeamId("all"); setStatus("all"); setDFrom(""); setDTo("");
    };

    const playedCount = filtered.filter((m) => m.status === "played").length;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="calendar-page">
            <div className="mb-6">
                <div className="op-label text-[10px] text-t2bb6ff">// TORNEO</div>
                <h1 className="text-4xl sm:text-5xl font-bold text-tffffff mt-1">Calendario Generale</h1>
                <p className="text-t8f95a3 text-sm mt-2 max-w-2xl">
                    Tutte le partite di campionato e coppe, in ordine cronologico. Verifica lo sviluppo del calendario e scopri quando giocano le squadre.
                </p>
            </div>

            {/* Filtri */}
            <div className="op-card p-4 mb-6" data-testid="calendar-filters">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <div>
                        <label className="op-label text-[9px] text-t5c6370 block mb-1">Competizione</label>
                        <select value={kind} onChange={(e) => { setKind(e.target.value); setCompetitionId("all"); }} className="w-full bg-t0e0f12 border border-t22252a text-tffffff text-xs px-2 py-2 focus:border-t2bb6ff outline-none" data-testid="filter-kind">
                            <option value="all">Tutte</option>
                            <option value="league">Solo Campionato</option>
                            <option value="cup">Solo Coppe</option>
                        </select>
                    </div>
                    <div className="col-span-1 md:col-span-2">
                        <label className="op-label text-[9px] text-t5c6370 block mb-1">Torneo</label>
                        <select value={competitionId} onChange={(e) => setCompetitionId(e.target.value)} className="w-full bg-t0e0f12 border border-t22252a text-tffffff text-xs px-2 py-2 focus:border-t2bb6ff outline-none" data-testid="filter-competition">
                            <option value="all">Tutti i tornei</option>
                            {competitions.map((c) => (
                                <option key={`${c.kind}-${c.id}`} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="op-label text-[9px] text-t5c6370 block mb-1">Squadra</label>
                        <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className="w-full bg-t0e0f12 border border-t22252a text-tffffff text-xs px-2 py-2 focus:border-t2bb6ff outline-none" data-testid="filter-team">
                            <option value="all">Tutte</option>
                            {data.teams.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="op-label text-[9px] text-t5c6370 block mb-1">Stato</label>
                        <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-t0e0f12 border border-t22252a text-tffffff text-xs px-2 py-2 focus:border-t2bb6ff outline-none" data-testid="filter-status">
                            <option value="all">Tutte</option>
                            <option value="scheduled">Da giocare</option>
                            <option value="played">Giocate</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button onClick={resetFilters} className="w-full op-btn op-btn-ghost text-xs py-2" data-testid="filter-reset">Azzera filtri</button>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-3">
                    <div>
                        <label className="op-label text-[9px] text-t5c6370 block mb-1">Dal</label>
                        <input type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)} className="w-full bg-t0e0f12 border border-t22252a text-tffffff text-xs px-2 py-2 focus:border-t2bb6ff outline-none" data-testid="filter-date-from" />
                    </div>
                    <div>
                        <label className="op-label text-[9px] text-t5c6370 block mb-1">Al</label>
                        <input type="date" value={dTo} onChange={(e) => setDTo(e.target.value)} className="w-full bg-t0e0f12 border border-t22252a text-tffffff text-xs px-2 py-2 focus:border-t2bb6ff outline-none" data-testid="filter-date-to" />
                    </div>
                </div>
            </div>

            <div className="op-mono text-[11px] text-t8f95a3 mb-3" data-testid="calendar-count">
                {filtered.length} partite · {playedCount} giocate · {filtered.length - playedCount} da giocare
            </div>

            {loading ? (
                <div className="op-mono text-sm text-t8f95a3 py-12 text-center">Caricamento calendario…</div>
            ) : groups.length === 0 ? (
                <div className="op-card p-8 text-center op-mono text-sm text-t8f95a3">Nessuna partita con questi filtri.</div>
            ) : (
                <div className="space-y-6">
                    {groups.map(([date, matches]) => (
                        <div key={date} data-testid={`calendar-day-${date}`}>
                            <div className="flex items-center gap-3 mb-2">
                                <span className="op-label text-[11px] text-t2bb6ff capitalize">{fmtDateHeader(date === "zzzz" ? null : date)}</span>
                                <span className="h-px flex-1 bg-t16181c" />
                                <span className="op-mono text-[10px] text-t5c6370">{matches.length} match</span>
                            </div>
                            <div className="op-card divide-y divide-t16181c">
                                {matches.map((m) => {
                                    const played = m.status === "played";
                                    const compLink = m.kind === "league" ? null : `/coppe/${m.competition_id}`;
                                    if (m.is_placeholder) {
                                        return (
                                            <div key={`${m.kind}-${m.id}`} className="flex items-center gap-3 px-3 py-2.5 bg-t0e0f12/30 hover:bg-t0e0f12/60 transition-colors" data-testid="calendar-placeholder-row">
                                                <div className="w-40 shrink-0 hidden sm:block">
                                                    <span className="op-label text-[9px] px-1.5 py-0.5 border" style={{ color: acc(m.competition_color), borderColor: `${withAlpha(acc(m.competition_color), "55")}` }}>
                                                        {m.competition_name}
                                                    </span>
                                                    <div className="op-mono text-[9px] text-t5c6370 mt-1 truncate">{m.round_label}</div>
                                                </div>
                                                <div className="flex-1 flex items-center justify-center gap-2 min-w-0">
                                                    <span className="w-5 h-5 shrink-0 rounded-full bg-t16181c border border-dashed border-t3a3d44" />
                                                    <span className="op-mono text-[11px] text-t8f95a3 italic">Squadre da sorteggiare</span>
                                                    <span className="op-mono text-[10px] text-t5c6370">vs</span>
                                                    <span className="w-5 h-5 shrink-0 rounded-full bg-t16181c border border-dashed border-t3a3d44" />
                                                </div>
                                                <div className="w-24 shrink-0 text-right">
                                                    <span className="op-mono text-[10px] text-tffb020">{m.giornata_from === m.giornata_to ? `G${m.giornata_from}` : `G${m.giornata_from}–G${m.giornata_to}`}</span>
                                                    {compLink && (
                                                        <Link to={compLink} className="block op-mono text-[9px] text-t2bb6ff hover:underline mt-1">dettaglio →</Link>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={`${m.kind}-${m.id}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-t0e0f12/60 transition-colors" data-testid="calendar-match-row">
                                            <div className="w-40 shrink-0 hidden sm:block">
                                                <span className="op-label text-[9px] px-1.5 py-0.5 border" style={{ color: acc(m.competition_color), borderColor: `${withAlpha(acc(m.competition_color), "55")}` }}>
                                                    {m.competition_name}
                                                </span>
                                                <div className="op-mono text-[9px] text-t5c6370 mt-1 truncate">{m.round_label}</div>
                                            </div>
                                            <div className="flex-1 grid grid-cols-[1fr_auto_1fr] items-center gap-2 min-w-0">
                                                <TeamCell name={m.home_team_name} logo={m.home_logo} align="right" />
                                                <div className="px-2 text-center shrink-0">
                                                    {played ? (
                                                        <span className="op-mono text-sm font-bold text-tffffff tabular-nums">{m.home_score}–{m.away_score}</span>
                                                    ) : (
                                                        <span className="op-mono text-[10px] text-t5c6370">vs</span>
                                                    )}
                                                </div>
                                                <TeamCell name={m.away_team_name} logo={m.away_logo} align="left" />
                                            </div>
                                            <div className="w-24 shrink-0 text-right">
                                                {played ? (
                                                    m.auto_result ? (
                                                        <TavolinoBadge idSuffix={m.id} definitive={m.is_definitive} />
                                                    ) : (
                                                        <span className="op-label text-[9px] text-t7ce38b border border-t7ce38b/40 px-1.5 py-0.5">Giocata</span>
                                                    )
                                                ) : m.postponed_absence ? (
                                                    <span
                                                        className="op-label text-[9px] text-tb084ff border border-tb084ff/50 px-1.5 py-0.5 inline-block"
                                                        title={`${m.postponed_absence.absent_team_name} in assenza · rinviata al ${fmtShort(m.date_from)}`}
                                                        data-testid={`calendar-absence-${m.id}`}
                                                    >
                                                        🗓️ IN ASSENZA · {fmtShort(m.date_from)}
                                                    </span>
                                                ) : (
                                                    <span className="op-mono text-[10px] text-tffb020">dal {fmtShort(m.date_from)}</span>
                                                )}
                                                {played && m.modifiable_until && !m.is_definitive && (
                                                    <span
                                                        className="op-label text-[9px] text-tffb020 border border-tffb020/40 px-1.5 py-0.5 inline-block mt-1"
                                                        title="Il risultato (incluso il tavolino automatico) può ancora essere corretto/contestato entro questa data. La finestra si allunga con i giorni di assenza."
                                                        data-testid={`calendar-modifiable-${m.id}`}
                                                    >
                                                        Modificabile fino al {fmtShort(m.modifiable_until)}
                                                    </span>
                                                )}
                                                {played && !m.auto_result && m.is_definitive && m.modifiable_until && (
                                                    <span
                                                        className="op-label text-[9px] text-t5c6370 border border-t5c6370/40 px-1.5 py-0.5 inline-block mt-1"
                                                        title="Finestra di ricorso/correzione scaduta: risultato definitivo"
                                                        data-testid={`calendar-definitive-${m.id}`}
                                                    >
                                                        DEFINITIVO
                                                    </span>
                                                )}
                                                {compLink && (
                                                    <Link to={compLink} className="block op-mono text-[9px] text-t2bb6ff hover:underline mt-1">dettaglio →</Link>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
